import type { ProductTimelineEntry } from '../types/meta-delivery.types';

import { deriveVerdictKind, isEpochSentinel, wasSentToMeta } from './timeline-verdict';

/** The four stages a product passes through in one delivery job. */
export type LifecycleStage = 'prepared' | 'sent' | 'ingested' | 'approval';

/** Where a stage got to. `skipped` = never reached (an earlier stage stopped it). */
export type LifecycleState = 'done' | 'failed' | 'warning' | 'pending' | 'skipped';

export interface LifecycleStep {
  stage: LifecycleStage;
  state: LifecycleState;
  /** i18n key suffix under `productLog.lifecycle.detail.*` describing this state. */
  detail: string;
  /** Only times the audit actually records; null means "not recorded", never "now". */
  at: string | null;
}

/**
 * Fold one audit record into a four-stage lifecycle — prepared → sent →
 * ingested by the destination → approval. Built only from fields the audit row
 * has: the send time (`deliveryTimestamp`) and the destination's last check
 * (`syncStatusAt`, which both the ingest verdict and the approval come from).
 * Intermediate transitions aren't stored (the audit row is overwritten in
 * place), so no other times are shown or invented.
 */
export function deriveLifecycle(entry: ProductTimelineEntry): LifecycleStep[] {
  const kind = deriveVerdictKind(entry);
  const checkedAt = isEpochSentinel(entry.syncStatusAt) ? null : entry.syncStatusAt;
  const isMock = entry.runMode === 'MOCK';

  // 1 · prepared — our own gate (validation, withheld, excluded).
  const stopped =
    kind === 'validation_failed'
      ? 'validationFailed'
      : kind === 'withheld'
        ? 'withheld'
        : kind === 'excluded'
          ? 'excluded'
          : null;
  const prepared: LifecycleStep = {
    stage: 'prepared',
    state: stopped ? (kind === 'excluded' ? 'skipped' : 'failed') : 'done',
    detail: stopped ?? 'ready',
    at: null,
  };

  // 2 · sent — handed to the destination (API push / file pushed).
  let sent: LifecycleStep;
  if (stopped) sent = { stage: 'sent', state: 'skipped', detail: 'notSent', at: null };
  else if (isMock) sent = { stage: 'sent', state: 'skipped', detail: 'dryRun', at: null };
  else if (kind === 'skipped_unchanged')
    sent = { stage: 'sent', state: 'skipped', detail: 'unchanged', at: null };
  else if (kind === 'queued')
    sent = { stage: 'sent', state: 'pending', detail: 'queued', at: null };
  else if (kind === 'transport_failed')
    sent = {
      stage: 'sent',
      state: 'failed',
      detail: 'transportFailed',
      at: entry.deliveryTimestamp,
    };
  else if (kind === 'rejected')
    sent = { stage: 'sent', state: 'failed', detail: 'rejected', at: entry.deliveryTimestamp };
  else if (wasSentToMeta(entry))
    sent = { stage: 'sent', state: 'done', detail: 'sent', at: entry.deliveryTimestamp };
  else sent = { stage: 'sent', state: 'failed', detail: 'failed', at: entry.deliveryTimestamp };

  const reached = sent.state === 'done';

  // 3 · ingested — the destination holds it (PERSISTED) or can't attach it (AWAITING_OWNERSHIP).
  let ingested: LifecycleStep;
  if (!reached) ingested = { stage: 'ingested', state: 'skipped', detail: 'notSent', at: null };
  else if (entry.ingestStatus === 'PERSISTED')
    ingested = { stage: 'ingested', state: 'done', detail: 'persisted', at: checkedAt };
  else if (entry.ingestStatus === 'AWAITING_OWNERSHIP')
    ingested = { stage: 'ingested', state: 'warning', detail: 'awaitingOwnership', at: checkedAt };
  else ingested = { stage: 'ingested', state: 'pending', detail: 'awaiting', at: null };

  // 4 · approval — the destination's verdict (syncStatus), separate from ingestion.
  let approval: LifecycleStep;
  const sync = entry.syncStatus.toUpperCase();
  if (!reached) approval = { stage: 'approval', state: 'skipped', detail: 'notSent', at: null };
  else if (sync === 'APPROVED')
    approval = { stage: 'approval', state: 'done', detail: 'approved', at: checkedAt };
  else if (sync === 'REJECTED' || sync === 'DISAPPROVED')
    approval = { stage: 'approval', state: 'failed', detail: 'rejected', at: checkedAt };
  else if (sync === 'UNKNOWN')
    approval = { stage: 'approval', state: 'warning', detail: 'unknown', at: checkedAt };
  else approval = { stage: 'approval', state: 'pending', detail: 'pending', at: checkedAt };

  return [prepared, sent, ingested, approval];
}
